import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdminHttpClient } from '../http.js';
import type { ToastMessage } from '../types.js';
import { describeError } from '../errors.js';
import { ToolActivationTab } from './platform/ToolActivationTab.js';
import { ExtensionReleasesTab } from './platform/ExtensionReleasesTab.js';

/**
 * Extension apps console — the single home for tenant extension-bundle
 * activation (e.g. HWP) across all three tiers:
 *  - platform admin: edits any acted-into tenant + manages stable releases
 *  - partner admin: edits its own acted-into tenant within the granted scope
 *  - tenant owner/admin: read-only view of what is active
 *
 * The wire contract stays `active_mcp_connectors`; ToolActivationTab projects
 * it as extension bundles. Editing routes through the operator token (platform
 * or partner surface); the tenant tier reads its own tenant via
 * `getOwnMcpConnectors` and renders read-only.
 */
export function ExtensionsScreen({
  platformHttp,
  tenantHttp,
  activeTenantId,
  isPlatformAdmin,
  isPartnerAdmin,
  onToast
}: {
  platformHttp: AdminHttpClient;
  tenantHttp: AdminHttpClient;
  activeTenantId?: string;
  isPlatformAdmin: boolean;
  isPartnerAdmin: boolean;
  onToast: (toast: ToastMessage) => void;
}) {
  const { t } = useTranslation();
  const isOperator = isPlatformAdmin || isPartnerAdmin;
  const tenantId = activeTenantId ?? '';
  const [assignable, setAssignable] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const connectors = isPlatformAdmin
        ? await platformHttp.getTenantMcpConnectors(tenantId)
        : isPartnerAdmin
          ? await platformHttp.partnerGetTenantMcpConnectors(tenantId)
          : await tenantHttp.getOwnMcpConnectors();
      setAssignable(connectors.assignableMcpConnectors);
      setSelected(connectors.activeMcpConnectors);
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, t) });
    }
  }, [isPlatformAdmin, isPartnerAdmin, platformHttp, tenantHttp, tenantId, onToast, t]);

  useEffect(() => {
    if (isOperator && !tenantId) return;
    void load();
  }, [isOperator, tenantId, load]);

  const save = async () => {
    if (!isOperator || !tenantId) return;
    setBusy(true);
    try {
      if (isPlatformAdmin) await platformHttp.setTenantMcpConnectors(tenantId, selected);
      else await platformHttp.partnerSetTenantMcpConnectors(tenantId, selected);
      await load();
      onToast({ tone: 'success', message: t('common.saved') });
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, t) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="screen-stack">
      <div className="section-heading">
        <div>
          <h2>{t('extensionApps.title')}</h2>
          <p>{t('extensionApps.help')}</p>
        </div>
      </div>
      <ToolActivationTab
        tenantId={tenantId}
        assignable={assignable}
        selected={selected}
        setSelected={setSelected}
        busy={busy}
        onSave={save}
        readOnly={!isOperator}
      />
      {isPlatformAdmin ? (
        <ExtensionReleasesTab http={platformHttp} onToast={onToast} isActive />
      ) : null}
    </section>
  );
}
